import {
  AnalyticsApi,
  AnalyticsContext,
  AnalyticsEvent,
  AnalyticsEventParam,
  AnalyticsEventType,
  AnalyticsProps,
  AnalyticsTechInitEvent,
  createAnalyticsContext,
  PluginNotReadyError,
  PluginType,
} from '@accedo/one-plugin-sdk';
import { ErrorBoundary } from '@dynamic/ui';
import {
  createRef,
  ReactNode,
  RefObject,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { useGlobalContext } from '#/components/useGlobalContext';
import { useConfig } from '#/hooks';
import { FeatureArrayData } from '#/service/config';
import { FeatureAnalyticsProps } from '#/service/config/features.types';
import { getCcpaOutOutSetting } from '#/service/user/settings';

import pluginManager from '../pluginManager';
import {
  getPayloadsForStructuredEvents,
  getPayloadsForUnstructuredEvents,
  mergeAnalyticsFeatureWithIntegrations,
  normalizeAnalyticsEvent,
} from './analyticsUtils';
import { useAppAnalyticsContext } from './useAppAnalyticsContext';

const onError = (error: Error, stack: string) => {
  console.error('Plugin error', error, stack);
};

const Hidden = styled.div`
  display: none;
`;

Hidden.displayName = 'Hidden';

const createComponents = (
  analyticsFeatures: FeatureArrayData<FeatureAnalyticsProps>
) => {
  // TODO: instead of "parallel" arrays combine these into one
  const newComponents: ReactNode[] = [];
  const newApiRefs: RefObject<AnalyticsApi>[] = [];
  const newConfig: FeatureArrayData<FeatureAnalyticsProps> = [];
  const ccpaOptOut = getCcpaOutOutSetting();

  analyticsFeatures
    .filter((feature) => feature.enabled && feature.plugin)
    .forEach((feature) => {
      const Component = pluginManager.getPluginComponent<AnalyticsProps>({
        type: PluginType.Analytics,
        id: feature.plugin?.id,
        optional: true,
      });

      const apiRef = createRef<AnalyticsApi>();

      newComponents.push(
        <ErrorBoundary key={feature.key} onError={onError}>
          <Suspense fallback={null}>
            <Component apiRef={apiRef} ccpaOptOut={ccpaOptOut} platform="web" />
          </Suspense>
        </ErrorBoundary>
      );
      newApiRefs.push(apiRef);
      newConfig.push(feature);
    });

  return {
    newComponents,
    newApiRefs,
    newConfig,
  };
};

// we have two kinds of buffering:
// - pre-init buffering: events that are sent before the app is booted and at least one plugin api ref is ready.
//   we store these events in `preInitBuffer` and retry them later, one time only, because at that point the event
//   buffer can take over.
// - event buffering: events that are sent after the app is booted, but not all plugins are ready yet.
//   we store event retry functions in `eventBuffer` and keep retrying them via `bufferLoop`.

const preInitBuffer: Event[] = [];

const TIMEOUT = 1000;

let timeoutId: number;

type EventCallback = () => void;

let eventBuffer: EventCallback[] = [];

// loops through `eventBuffer` and sends events
// re-sends events that were rejected with `PluginNotReadyError`
const bufferLoop = () => {
  clearTimeout(timeoutId);

  eventBuffer = eventBuffer.reduce(
    (queue: EventCallback[], sendEvent: EventCallback) => {
      try {
        sendEvent();
      } catch (error) {
        if (error.name === 'PluginNotReadyError') {
          queue.push(sendEvent);
        } else {
          console.error(error);
        }
      }

      return queue;
    },
    []
  );

  if (eventBuffer.length) {
    timeoutId = window.setTimeout(bufferLoop, TIMEOUT);
  }
};

const AnalyticsManager = () => {
  const globalContext = useGlobalContext();
  const originalConfig = useConfig();
  const [components, setComponents] = useState<ReactNode[]>([]);
  const [apiRefs, setApiRefs] = useState<RefObject<AnalyticsApi>[]>([]);
  const [config, setConfig] = useState<FeatureArrayData<FeatureAnalyticsProps>>(
    []
  );
  const appContext = useAppAnalyticsContext();

  const {
    boot: { isBooted },
  } = globalContext;

  // we need a ready flag in `onAnalyticsEvent` but we don't want to re-create the callback
  // because that would mess with the retry logic.
  const isReadyRef = useRef(false);
  isReadyRef.current = isBooted && apiRefs.length > 0;

  const analytics = useMemo(
    () => mergeAnalyticsFeatureWithIntegrations(originalConfig),
    [originalConfig]
  );

  // event handler for analytics events. sending event data to all analytics plugins.
  const onAnalyticsEvent = useCallback(
    (evt: Event) => {
      // typescript issue requires casting: https://github.com/microsoft/TypeScript/issues/28357
      const analyticsEvent = evt as AnalyticsEvent;
      const detail = analyticsEvent.detail;
      // Recreate the context when sending an event
      const analyticsContext: AnalyticsContext = createAnalyticsContext();

      if (!detail) {
        return;
      }
      console.debug('AnalyticsManager - Forwarding event to plugins.', detail);

      const { event, payload, isTechnical } = detail;

      const populatedContext = Object.assign(
        analyticsContext,
        payload,
        appContext
      );

      // store and retry pre-init events. see explanation at `preInitBuffer` declaration
      if (!isReadyRef.current) {
        preInitBuffer.push(evt);

        return;
      }

      // sending the event to all plugins that are configured to receive it
      apiRefs.forEach((apiRef, index) => {
        const analyticsProps = config[index].properties;
        const isS3Config = Boolean(analyticsProps.events?.[event]);
        const isCoreConfig = Boolean(analyticsProps.structuredEvents?.[event]);
        const isConfigured = isS3Config || isCoreConfig;

        // ignore non-tech event if no plugin is configured to receive it
        if (!isConfigured && !isTechnical) {
          return;
        }

        // create the payloads
        const pluginId = config[index].plugin?.id;
        const isStructured = analyticsProps?.structuredEvents?.[event]?.length;
        const payloadsToSend = isStructured
          ? getPayloadsForStructuredEvents({
              analyticsProps,
              analyticsEvent,
              analyticsContext,
              pluginId,
              event,
            })
          : getPayloadsForUnstructuredEvents({
              analyticsProps,
              analyticsEvent,
              populatedContext,
              pluginId,
            });

        // push all the outgoing events to the buffer
        payloadsToSend.forEach((customPayload) => {
          const bufferedEvent = () => {
            if (!apiRef.current) {
              throw new PluginNotReadyError(pluginId || '');
            }
            apiRef.current.sendEvent(
              event,
              normalizeAnalyticsEvent(customPayload)
            );
          };

          eventBuffer.push(bufferedEvent);
        });

        // try to send everything in the buffer
        bufferLoop();
      });

      // removing user-related params from the context after logging out.
      // follows the `logout` function in the root of `auth.ts` which removes the same values from the auth store.
      if (event === AnalyticsEventType.ActionLogout) {
        analyticsContext[AnalyticsEventParam.UserId] = '';
        analyticsContext[AnalyticsEventParam.UserProfile] = null;
        analyticsContext[AnalyticsEventParam.SessionId] = '';
      }

      // the first event after the init is TechInit. now that we sent that out, we can process the pre-init events.
      if (preInitBuffer.length) {
        // remove all from the buffer first, so we don't end up here again in the next run.
        preInitBuffer.splice(0).forEach(onAnalyticsEvent);
      }
    },
    [appContext, apiRefs, config]
  );

  useEffect(() => {
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  // add/remove event listener on mount/unmount
  useEffect(() => {
    document.addEventListener('analytics', onAnalyticsEvent);

    return () => {
      document.removeEventListener('analytics', onAnalyticsEvent);
    };
  }, [onAnalyticsEvent]);

  // create the wrapped plugin components and api refs
  useEffect(() => {
    if (isBooted) {
      const { newComponents, newApiRefs, newConfig } =
        createComponents(analytics);
      setComponents(newComponents);
      setApiRefs(newApiRefs);
      setConfig(newConfig);
    }
  }, [analytics, isBooted]);

  // dispatch init event
  useEffect(() => {
    if (apiRefs.length) {
      document.dispatchEvent(new AnalyticsTechInitEvent());
    }
  }, [apiRefs]);

  return <Hidden>{components}</Hidden>;
};

AnalyticsManager.displayName = 'AnalyticsManager';

export default AnalyticsManager;
